import classnames from 'classnames'
import React, { type PropsWithChildren, useMemo } from 'react'

import { TtIcon, TtVideoCard } from '@23/react'

import { useVideoQuery } from '../../../blocs/useVideo'

import './VideoPickerCard.scss'

interface VideoCardProps {
  id: number // use id 0 or undefined for a placeholder card
  className?: string
  preview?: boolean
  selected?: boolean
  selectable?: boolean
  isAnchor?: boolean
  onRemoveClick?: () => void
}

interface PlaceholderCardProps {
  className?: string
}

const PlaceholderCard = ({ className }: PlaceholderCardProps) => {
  return (
    <div className={classnames(['placeholder-video-card', className])}>
      <TtIcon library="one" name="video" />
    </div>
  )
}

const VideoPickerCard = ({
  id,
  className,
  preview = false,
  selected = false,
  selectable = false,
  isAnchor = false,
  onRemoveClick,
}: PropsWithChildren<VideoCardProps>) => {
  const { data: video, isLoading } = useVideoQuery(id)

  const thumbnailURL = useMemo(() => {
    if (!video) return ''

    const stub = video.treeId || 'thumbnail'
    const url = `/${stub}/${id}/${video.token}/400x300cr/thumbnail.jpeg`

    if (import.meta.env.DEV) {
      return `https://localhost:8090${url}`
    }

    return url
  }, [id, video])

  if (isLoading || !video) {
    return <PlaceholderCard className={className} />
  }

  if (video.isDeleted) {
    return (
      <TtVideoCard
        className={className}
        videoId={`${id}`}
        cardTitle={video.title}
        // @ts-expect-error — TODO: fix this in the ui library side
        metricsIcon={null}
        state="deleted"
        showRemoveButton={!!onRemoveClick}
        onTt-remove={onRemoveClick}
      />
    )
  }

  return (
    <TtVideoCard
      className={className}
      videoId={`${id}`}
      state={video.isPublished ? 'published' : 'unpublished'}
      timing={video.durationSeconds}
      metricsLabel={`${video.viewCount}`}
      thumbnail={thumbnailURL}
      preview={preview ? video.previewFrames : undefined}
      category={video.categoryName}
      cardTitle={video.title}
      dateTime={video.creationDate}
      selected={selected}
      selectable={selectable}
      showRemoveButton={!!onRemoveClick}
      onTt-remove={onRemoveClick}
      data-history-href={isAnchor ? `/manage/video/${video.id}` : undefined}
    />
  )
}

const VideoCardContainer = ({
  id,
  className,
  preview,
  selected,
  selectable,
  isAnchor,
  onRemoveClick,
}: PropsWithChildren<Partial<VideoCardProps>>) => {
  if (!id) {
    return <PlaceholderCard className={className} />
  }

  return (
    <VideoPickerCard
      id={id}
      className={className}
      preview={preview}
      selected={selected}
      selectable={selectable}
      isAnchor={isAnchor}
      onRemoveClick={onRemoveClick}
    />
  )
}

export { VideoCardContainer as VideoCard }
